// src/catalog/utils/catalogAccess.js

import { serviceRoutesById } from "../../navigation/publicServiceRoutes";

const UNAVAILABLE_LIFECYCLE_LABELS = {
  comingSoon: "Coming soon",
  closed: "Closed",
  archived: "Archived"
};

const BLOCKED_PARTICIPATION_LABELS = {
  closed: "Closed",
  archived: "Archived"
};

const RESTRICTED_PARTICIPATION_MODES = new Set([
  "applicationBased",
  "institutionAssigned",
  "invitationOnly"
]);

const RESTRICTED_ACCESS_MODELS = new Set([
  "paidIndividual",
  "paidAccess",
  "institutionalLicense",
  "researchCollaboration",
  "customQuote",
  "internal"
]);

const RESTRICTED_COMMERCIAL_ACCESS_MODELS = new Set([
  "paidIndividual",
  "institutionalLicense",
  "customQuote"
]);

const RESTRICTED_PRICING_MODELS = new Set([
  "oneTime",
  "subscription",
  "usageBased",
  "seatBased",
  "cohortBased",
  "institutional",
  "custom"
]);

const CATALOG_DISCOVERY_ACTIONS_BY_SLUG = Object.freeze({
  "perceptual-skill-training": Object.freeze({
    type: "catalogDiscovery",
    label: "Explore activities",
    disabled: false,
    reason: "exploreTrainingActivities",
    to: "/app/catalog?type=activity",
    filter: Object.freeze({
      primaryType: "activity"
    })
  })
});

const SERVICE_EDITORIAL_ROUTE_IDS_BY_SLUG = Object.freeze({
  "perceptual-skill-training": "perceptual-skill-development",
  "institutional-training-programs": "institutional-programs",
  "custom-perceptual-experiments": "perceptual-research-infrastructure",
  "perceptual-data-annotation": "perceptual-data-annotation"
});

function hasLaunchConfiguration(entry) {
  if (!entry) return false;

  if (entry.launch?.type === "appView") {
    return Boolean(entry.launch.viewId || entry.slug);
  }

  if (entry.launch?.type === "project") {
    return Boolean(entry.launch.route || entry.source?.slug);
  }

  if (entry.launch?.type === "externalUrl") {
    return Boolean(entry.launch.externalUrl);
  }

  if (entry.source?.type === "project" && entry.source?.slug) {
    return true;
  }

  return false;
}

function hasProjectMemberContinuation(entry) {
  return Boolean(
    entry?.viewerContext?.isProjectMember === true &&
    entry?.source?.type === "project" &&
    entry?.source?.slug &&
    hasLaunchConfiguration(entry)
  );
}

export function isProjectAccessCodeEnrollmentEntry(entry) {
  return Boolean(
    entry?.participationMode === "accessCodeRequired" &&
    entry?.source?.type === "project" &&
    entry?.source?.slug &&
    entry?.viewerContext?.isProjectMember !== true
  );
}

export function isProjectAccessCodeAction(action) {
  return action?.type === "projectAccessCode";
}

function hasEntitlementRequirement(entry) {
  const accessRequirements = entry?.accessRequirements || {};
  const acceptedEntitlementTypes =
    accessRequirements.acceptedEntitlementTypes;

  return (
    Array.isArray(acceptedEntitlementTypes) &&
    acceptedEntitlementTypes.length > 0
  );
}

function requiresUnavailableContext(entry) {
  const accessRequirements = entry?.accessRequirements || {};

  return Boolean(
    accessRequirements.requiresInstitutionAssignment ||
    accessRequirements.requiresProjectContext ||
    accessRequirements.requiresProgramContext ||
    accessRequirements.requiresCohortContext
  );
}

function hasRestrictedCommercialModel(entry) {
  const commercial = entry?.commercial || {};
  const offers = Array.isArray(commercial.offers)
    ? commercial.offers
    : [];

  return offers
    .filter((offer) => offer?.isActive !== false)
    .some((offer) => (
      RESTRICTED_COMMERCIAL_ACCESS_MODELS.has(offer.accessModel) ||
      RESTRICTED_PRICING_MODELS.has(offer.pricingModel) ||
      offer.pricingDisplay === "contact"
    ));
}

function hasRestrictedAccessModel(entry) {
  return RESTRICTED_ACCESS_MODELS.has(entry?.accessModel);
}

function hasRestrictedParticipationMode(entry) {
  return RESTRICTED_PARTICIPATION_MODES.has(entry?.participationMode);
}

function getLaunchLabel(entry) {
  if (entry?.primaryType === "program") {
    return "Open program";
  }

  if (hasProjectMemberContinuation(entry)) {
    return "Open project";
  }

  if (entry?.primaryType === "resource") {
    return "Open resource";
  }

  return "Open";
}

function getContactActionLabel() {
  return "Contact us";
}

function getContactActionReason(entry) {
  if (entry?.primaryType === "service") {
    return "service";
  }

  if (hasEntitlementRequirement(entry)) {
    return "entitlementRequired";
  }

  if (hasRestrictedAccessModel(entry)) {
    return "restrictedAccessModel";
  }

  if (hasRestrictedCommercialModel(entry)) {
    return "commercialAccess";
  }

  if (requiresUnavailableContext(entry)) {
    return "contextRequired";
  }

  if (hasRestrictedParticipationMode(entry)) {
    return "restrictedParticipation";
  }

  return "contact";
}

function getCatalogDiscoveryAction(entry) {
  if (!entry?.slug) {
    return null;
  }

  const action =
    CATALOG_DISCOVERY_ACTIONS_BY_SLUG[entry.slug];

  return action
    ? {
        ...action,
        filter: {
          ...(action.filter || {})
        }
      }
    : null;
}

export function getCatalogLearnMoreDestination(entry) {
  if (!entry?.slug) {
    return null;
  }

  if (entry.primaryType === "service") {
    const serviceRouteId =
      SERVICE_EDITORIAL_ROUTE_IDS_BY_SLUG[entry.slug];
    const serviceRoute =
      serviceRouteId
        ? serviceRoutesById[serviceRouteId]
        : null;

    if (serviceRoute?.path) {
      return {
        type: "editorial",
        to: serviceRoute.path,
        target: "_blank"
      };
    }
  }

  return {
    type: "catalogDetail",
    to: `/app/catalog/${entry.slug}`,
    target: "_self"
  };
}

export function getCatalogEntryPrimaryAction(
  entry
) {
  if (!entry) {
    return {
      type: "disabled",
      label: "Open",
      disabled: true,
      reason: "missingEntry"
    };
  }

  const lifecycleLabel =
    UNAVAILABLE_LIFECYCLE_LABELS[entry.lifecycleStatus];

  if (lifecycleLabel) {
    return {
      type: "disabled",
      label: lifecycleLabel,
      disabled: true,
      reason: entry.lifecycleStatus
    };
  }

  const participationLabel =
    BLOCKED_PARTICIPATION_LABELS[entry.participationMode];

  if (participationLabel) {
    return {
      type: "disabled",
      label: participationLabel,
      disabled: true,
      reason: entry.participationMode
    };
  }

  const catalogDiscoveryAction =
    getCatalogDiscoveryAction(entry);

  if (catalogDiscoveryAction) {
    return catalogDiscoveryAction;
  }

  if (hasProjectMemberContinuation(entry)) {
    return {
      type: "launch",
      label: getLaunchLabel(entry),
      disabled: false,
      reason: "projectMember"
    };
  }

  if (isProjectAccessCodeEnrollmentEntry(entry)) {
    return {
      type: "projectAccessCode",
      label: "Enter access code",
      disabled: false,
      reason: "accessCodeRequired"
    };
  }

  if (
    entry.primaryType === "service" ||
    hasEntitlementRequirement(entry) ||
    hasRestrictedAccessModel(entry) ||
    hasRestrictedCommercialModel(entry) ||
    requiresUnavailableContext(entry) ||
    hasRestrictedParticipationMode(entry)
  ) {
    const fallbackAction =
      entry.accessRequirements?.fallbackAction;

    if (
      entry.participationMode === "invitationOnly" &&
      fallbackAction !== "contact" &&
      fallbackAction !== "requestAccess"
    ) {
      return {
        type: "disabled",
        label: "Invitation required",
        disabled: true,
        reason: "invitationOnly"
      };
    }

    return {
      type: fallbackAction === "requestAccess"
        ? "requestAccess"
        : "contact",
      label: getContactActionLabel(),
      disabled: false,
      reason: getContactActionReason(entry)
    };
  }

  if (hasLaunchConfiguration(entry)) {
    return {
      type: "launch",
      label: getLaunchLabel(entry),
      disabled: false,
      reason: "launchable"
    };
  }

  return {
    type: "disabled",
    label: "Learn more",
    disabled: true,
    reason: "noLaunch"
  };
}

export function canLaunchCatalogEntry(entry) {
  return getCatalogEntryPrimaryAction(entry).type === "launch";
}

export function isCatalogContactAction(action) {
  return (
    action?.type === "contact" ||
    action?.type === "requestAccess"
  );
}

export function isCatalogDiscoveryAction(action) {
  return (
    action?.type === "catalogDiscovery" &&
    typeof action.to === "string" &&
    action.to.trim().length > 0
  );
}

export function buildCatalogContactMessage(entry, action) {
  if (!entry) return "";

  const title =
    entry.title ||
    entry.titleKey ||
    entry.slug ||
    "this catalog entry";

  const actionText =
    action?.type === "requestAccess" ||
    action?.label?.toLowerCase?.().includes("request")
      ? "request access to"
      : "ask about";

  return [
    `Hello,`,
    "",
    `I would like to ${actionText} "${title}".`,
    "",
    "Thank you."
  ]
    .filter((line) => line !== null && line !== undefined)
    .join("\n");
}
